import type { FC, HTMLAttributes } from 'react';
import { forwardRef, useState } from 'react';
import { Button, Dropdown } from 'antd';
import { TbPlus } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useResourceCollaborators } from '@/hooks/useResourceCollaborators';
import { useWorkspaceResourceAccess } from '@/hooks/useWorkspaceResourceAccess';
import DropdownPanel from '@/shared/components/dropdowns/DropdownPanel';
import { SmallTooltip } from '@/shared/components/tooltips';
import { ResourceCollaborator, ResourceType } from '@/stores/database/entities/00-RBAC/99-Collaborators/types';

import Invite from './views/01-Invite';
import type { InviteableResourceVisibility, InviteableWorkspaceResource } from './types';

interface InviteToResourceDropdownProps {
    onVisibilityChange?: (visibility: InviteableResourceVisibility) => Promise<void> | void;
    resource: InviteableWorkspaceResource;
    resourceType: ResourceType;
}

interface InviteToResourceButtonProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
    disabled?: boolean;
    open?: boolean;
}

/** The canonical resource-header collaborator trigger shared by every resource type. */
export const InviteToResourceButton = forwardRef<HTMLSpanElement, InviteToResourceButtonProps>(
    ({ disabled = false, open = false, style, ...spanProps }, ref) => {
        const { token } = useAntDesignTheme();

        return (
            <span ref={ref} {...spanProps} style={{ display: 'inline-flex', ...style }}>
                <SmallTooltip title={disabled ? 'You do not have permission to invite collaborators' : undefined}>
                    <Button
                        aria-label='Invite collaborators to resource'
                        disabled={disabled}
                        shape='circle'
                        type='dashed'
                    >
                        <TbPlus
                            size='1rem'
                            style={{
                                color: token.colorTextTertiary,
                                transform: open ? 'rotate(405deg)' : undefined,
                                transition: 'transform 750ms ease',
                                verticalAlign: '-0.175rem',
                            }}
                        />
                    </Button>
                </SmallTooltip>
            </span>
        );
    },
);

InviteToResourceButton.displayName = 'InviteToResourceButton';

const InviteToResourceDropdown: FC<InviteToResourceDropdownProps> = ({
    onVisibilityChange,
    resource,
    resourceType,
}) => {
    const { collaborators } = useResourceCollaborators(resourceType, resource.id);
    const access = useWorkspaceResourceAccess(resourceType, resource.id);

    const [open, setOpen] = useState(false);
    const disabled = access.invite.disabled;

    return (
        <Dropdown
            destroyOnHidden
            open={disabled ? false : open}
            onOpenChange={(nextOpen) => {
                if (!disabled) {
                    setOpen(nextOpen);
                }
            }}
            trigger={['click']}
            placement='bottomRight'
            popupRender={() => (
                <DropdownPanel
                    contentStyle={{ padding: '1rem' }}
                    style={{
                        marginTop: '0.5rem',
                    }}
                    width={16 * 36}
                >
                    <Invite
                        resource={resource}
                        resourceType={resourceType}
                        collaborators={collaborators as ResourceCollaborator[]}
                        canInvite={access.canInvite}
                        canManage={access.canManage}
                        onVisibilityChange={onVisibilityChange}
                    />
                </DropdownPanel>
            )}
        >
            <InviteToResourceButton disabled={disabled} open={open} />
        </Dropdown>
    );
};

export type { InviteableResourceVisibility, InviteableWorkspaceResource } from './types';
export default InviteToResourceDropdown;
